import { useState, useCallback, type MouseEvent } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';

import { useTheme } from '@mui/material/styles';
import { Box, Button, IconButton, MenuItem, Popover, Stack, Tooltip } from '@mui/material';
import { DataGrid, Toolbar } from '@mui/x-data-grid';

import Iconify from '../../components/iconify';
import Label from '../../components/label';
import { ToolBarSearch, ToolBarColumnToggler, Container as DataGridContainer } from '../../components/datagrid';
import { useGetSuppliersQuery, useDelSupplierMutation } from '../../slices/apis/testingCentreApi';
import type { SupplierProps } from '../../models';

type DataGridRow = {
	id: SupplierProps['id'];
	name: SupplierProps['name'];
	email: SupplierProps['email'];
	phone: SupplierProps['phone'];
	isBulk: SupplierProps['isBulk'];
};

export default function Suppliers() {
	const [menuOpen, setMenuOpen] = useState<HTMLButtonElement | null>(null);
	const [selectedSupplierId, setSelectedSupplierId] = useState<string | number | null>(null);

	const navigate = useNavigate();
	const theme = useTheme();
	const { data = [], isFetching } = useGetSuppliersQuery();
	const [delSupplier] = useDelSupplierMutation();

	const handleOpenMenu = (event: MouseEvent<HTMLButtonElement>) => {
		setMenuOpen(event.currentTarget);
		setSelectedSupplierId(event.currentTarget.getAttribute('data-supplier-id'));
	};

	const handleCloseMenu = () => {
		setMenuOpen(null);
	};

	const handleDeleteSupplier = () => {
		delSupplier(selectedSupplierId);
		setMenuOpen(null);
	};

	const handleEditSupplier = () => {
		navigate(`/dashboard/admin/suppliers/${selectedSupplierId}/edit`);
		setMenuOpen(null);
	};

	const handleViewSupplier = () => {
		navigate(`/dashboard/admin/suppliers/${selectedSupplierId}/view`);
		setMenuOpen(null);
	};

	const handleNavigate = useCallback(
		(e: MouseEvent<HTMLButtonElement>) => {
			navigate(e.currentTarget.getAttribute('data-route') as string);
		},
		[navigate],
	);

	return (
		<>
			<Helmet>
				<title>Testing Centres - {process.env.REACT_APP_ORG}</title>
			</Helmet>
			<Box
				sx={{
					width: { xs: '100%', md: '85%' },
					ml: { xs: 0, md: 3, lg: 4 },
					height: '100%',
					display: 'flex',
					flexDirection: 'column',
				}}
			>
				<Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
					<Button
						variant="contained"
						data-route="/dashboard/admin/suppliers/new"
						onClick={handleNavigate}
						startIcon={<Iconify icon="eva:plus-fill" />}
					>
						New Testing Centre
					</Button>
				</Stack>

				<DataGridContainer>
					<DataGrid
						disableColumnFilter
						disableDensitySelector
						disableColumnSelector
						showToolbar
						loading={isFetching}
						columnHeaderHeight={48}
						slots={{
							toolbar: () => (
								<Toolbar>
									<Box sx={{ pl: 1 }}>
										<ToolBarSearch />
									</Box>
									<Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
										<Tooltip title="Columns">
											<ToolBarColumnToggler />
										</Tooltip>
									</Box>
								</Toolbar>
							),
						}}
						sx={{
							width: '100%',
							borderRadius: 3,
							'& .MuiDataGrid-columnHeaderTitle': {
								whiteSpace: 'normal',
								lineHeight: 1.4,
								fontSize: '0.9rem',
								color: 'grey',
							},
							'& .MuiDataGrid-columnHeader': {
								backgroundColor: theme.palette.grey[100],
							},
						}}
						columns={[
							{
								field: 'id',
								headerName: 'ID',
								resizable: false,
								sortable: false,
								flex: 0.25,
							},
							{
								field: 'name',
								headerName: 'Name',
								resizable: false,
								flex: 1,
							},
							{
								field: 'email',
								headerName: 'Email',
								resizable: false,
								flex: 1,
							},
							{
								field: 'phone',
								headerName: 'Phone',
								resizable: false,
								flex: 0.7,
							},
							{
								field: 'isBulk',
								headerName: 'Bulk',
								resizable: false,
								flex: 0.4,
								renderCell: (params) => {
									return params.row.isBulk ? <Label color="success">BULK</Label> : null;
								},
							},
							{
								field: 'actions',
								headerName: '',
								headerAlign: 'center',
								align: 'right',
								flex: 0.3,
								sortable: false,
								filterable: false,
								resizable: false,
								renderCell: (params) => {
									const {
										row: { id },
									} = params;

									return (
										<IconButton size="large" color="inherit" data-supplier-id={id} onClick={handleOpenMenu}>
											<Iconify icon={'eva:more-vertical-fill'} />
										</IconButton>
									);
								},
							},
						]}
						rows={data.map((supplier) => {
							return {
								id: supplier.id,
								name: supplier.name,
								email: supplier.email,
								phone: supplier.phone,
								isBulk: supplier.isBulk,
								actions: '',
							} as DataGridRow;
						})}
					/>
				</DataGridContainer>
			</Box>

			<Popover
				open={Boolean(menuOpen)}
				anchorEl={menuOpen}
				onClose={handleCloseMenu}
				anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
				PaperProps={{
					sx: {
						p: 1,
						width: 140,
						'& .MuiMenuItem-root': {
							px: 1,
							typography: 'body2',
							borderRadius: 0.75,
						},
					},
				}}
			>
				<MenuItem onClick={handleViewSupplier}>
					<Iconify icon={'eva:file-text-outline'} sx={{ mr: 1 }} />
					View
				</MenuItem>
				<MenuItem onClick={handleEditSupplier}>
					<Iconify icon={'eva:edit-fill'} sx={{ mr: 1 }} />
					Edit
				</MenuItem>
				<MenuItem sx={{ color: 'error.main' }} onClick={handleDeleteSupplier}>
					<Iconify icon={'eva:trash-2-outline'} sx={{ mr: 1 }} />
					Delete
				</MenuItem>
			</Popover>
		</>
	);
}
